import { useSyncExternalStore } from 'react'
import { useMutation, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query'
import type { AgentAvatar, RouteName, RouteResponse } from '@algoboo/schema/api'
import { avatarsVersion, knownAvatar, request, subscribeAvatars, type ApiRequest } from '../client'
import { qk, SESSION_ROOTS } from '../keys'
import { clearRecent } from '../../search'
import { clearPendingSignOut } from '../signout'

// --- avatars ---------------------------------------------------------------------

/** The configured mascot for a handle if any response carried it (AgentSummary does not). */
export function useKnownAvatar(handle: string): AgentAvatar | undefined {
  useSyncExternalStore(subscribeAvatars, avatarsVersion, avatarsVersion)
  return knownAvatar(handle)
}

// --- mutations -------------------------------------------------------------------

export type MutationVars<N extends RouteName> = ApiRequest<N>

export interface ApiMutationOptions<N extends RouteName> {
  invalidate?: (vars: MutationVars<N>, data: RouteResponse<N>) => readonly QueryKey[]
  onSuccess?: (data: RouteResponse<N>, vars: MutationVars<N>) => void
}

export function useApiMutation<N extends RouteName>(name: N, options: ApiMutationOptions<N> = {}) {
  const qc = useQueryClient()
  return useMutation<RouteResponse<N>, Error, MutationVars<N>>({
    mutationKey: [name],
    mutationFn: (vars) => request(name, vars),
    onSuccess: async (data, vars) => {
      options.onSuccess?.(data, vars)
      const keys = options.invalidate?.(vars, data) ?? []
      await Promise.all(keys.map((queryKey) => qc.invalidateQueries({ queryKey })))
    },
  })
}

/** Common invalidation sets for mutations. */
export const invalidate = {
  follow: (handle: string) => [qk.agent(handle), qk.agents(), qk.following(), qk.me(), qk.weekStandings()],
  agent: (handle: string) => [qk.agent(handle), qk.agents(), qk.manage(handle), qk.agentVersions(handle), qk.feedRoot()],
  createAgent: () => [qk.agents(), qk.me()],
  subscribe: (handle: string) => [qk.agent(handle), qk.subscriptions(), qk.me()],
  wallets: () => [qk.accountWallet(), qk.portfolio(), qk.me()],
  permissions: () => [qk.permissions().slice(0, 2), qk.portfolio(), qk.subscriptions(), qk.me()],
  withdrawals: () => [qk.portfolio(), qk.walletStatement()],
  credits: () => [qk.credits(), qk.me()],
  payments: () => [qk.payments(), qk.credits(), qk.subscriptions(), qk.me()],
  token: (handle: string) => [qk.agentToken(handle), qk.agent(handle), qk.tokens(), qk.manage(handle)],
  claim: (handle: string) => [['claimable', handle], qk.agentTokenEpochs(handle)],
  apiKeys: () => [qk.myApiKeys(), qk.me()],
  linkedWallets: () => [qk.myWallets(), qk.me()],
  myAgents: (handle?: string) => [['me', 'agents'], qk.me(), ...(handle ? [qk.agent(handle), qk.manage(handle)] : [])],
  notifications: () => [['me', 'notifications']],
} satisfies Record<string, (...args: never[]) => readonly QueryKey[]>

/** Query roots outside SESSION_ROOTS whose answers depend on the viewer. */
const VIEWER_ROOTS = [qk.agents(), qk.feedRoot(), ['evidence'], ['search'], ['external'], ['creators']] as const

/** After sign-in or sign-out: drop session data and refetch viewer-dependent views. */
export async function resetSession(qc: QueryClient, me?: RouteResponse<'getMe'> | null): Promise<void> {
  for (const root of SESSION_ROOTS) {
    if (root[0] === 'me') continue
    qc.removeQueries({ queryKey: root })
  }
  qc.removeQueries({ queryKey: qk.me(), exact: false, predicate: (q) => q.queryKey.length > 1 })
  if (me !== undefined) qc.setQueryData(qk.me(), me)
  else await qc.invalidateQueries({ queryKey: qk.me(), exact: true })
  // Public reads the API still answers per viewer (a draft agent, its feed and evidence for its creator,
  // `viewer.isOwner`/`following`): reset, not only invalidated, so the previous session's answer never renders
  // while (or instead of, offline) the refetch runs (V-R5).
  await Promise.all(VIEWER_ROOTS.map((queryKey) => qc.resetQueries({ queryKey })))
  if (me === null) {
    clearRecent()
  } else if (me) {
    clearPendingSignOut() // signed in again: an older pending sign-out must not end this session
  }
}

